/**
 * Variant siblings (collection/search/cards)
 *
 * Goal:
 * - Products remain separate in collections/search (backend reality)
 * - Clicking a product routes to a canonical "master" PDP, keeping URL unique
 * - Master PDP swaps displayed product via AJAX without changing URL
 *
 * Strategy (no metafields):
 * - Infer a "base title" shared prefix by querying /search/suggest.json for candidate prefixes
 * - Choose the base that yields the largest sibling set (>= 2). On ties, prefer the *shortest* base
 *   (this avoids accidentally absorbing color adjectives like "Dark" in "Dark Grey" when ambiguous).
 * - Master is deterministic: smallest handle among siblings.
 *
 * Safety:
 * - If inference is uncertain (no base yielding >= 2 siblings), do nothing.
 */

(() => {
  const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim();
  const normLower = (s) => normalize(s).toLowerCase();
  const CONNECTOR_WORDS = new Set([
    'edizione',
    'limitata',
    'charm',
    'in',
    // common EN connectors that shouldn't start a "color"
    'limited',
    'edition',
    // materials/model words that should never start the "color" suffix
    'pelle',
    'liscia',
    'liscio',
    // model/material words that should never be treated as "color"
    'suede',
    'monogrammata',
    'monogrammato',
    'monogramm',
  ]);

  const productCardSelector = 'product-card[data-product-handle][data-product-title][data-product-url]';
  const processedAttr = 'data-variant-siblings-processed';

  const suggestCache = new Map(); // key: query -> Promise<products[]>

  // More reliable than /search/suggest.json: use a theme endpoint backed by storefront search.
  async function fetchSearchProducts(query) {
    const q = normalize(query);
    if (!q) return [];
    if (suggestCache.has(q)) return await suggestCache.get(q);

    const p = (async () => {
      const toItems = (raw) =>
        (raw || [])
          .map((r) => ({
            title: normalize(r.title),
            titleLower: normLower(r.title),
            url: r.url,
            image: r.image || r.featured_image || null,
            handle:
              r.handle ||
              (r.url ? r.url.split('?')[0].split('/').filter(Boolean).pop() : null),
          }))
          .filter((x) => x.handle && x.url && x.title);

      const fetchPage = async (page) => {
        const url =
          `/search?view=vs-siblings` +
          `&type=product` +
          `&options[prefix]=last` +
          `&options[unavailable_products]=show` +
          `&page=${page}` +
          `&q=${encodeURIComponent(q)}`;

        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return { pages: 1, items: [] };
        const txt = await res.text();
        let data = null;
        try {
          data = JSON.parse(txt);
        } catch {
          return { pages: 1, items: [] };
        }
        return { pages: Number(data?.pages || 1) || 1, items: toItems(data?.products) };
      };

      const first = await fetchPage(1);
      const map = new Map(first.items.map((it) => [it.handle, it]));

      const totalPages = Math.max(1, first.pages);
      // Safety cap against very large catalogs; base queries should keep pages small.
      const cap = Math.min(totalPages, 20);
      for (let page = 2; page <= cap; page++) {
        const next = await fetchPage(page);
        next.items.forEach((it) => map.set(it.handle, it));
      }

      return Array.from(map.values());
    })();

    suggestCache.set(q, p);
    return await p;
  }

  function candidatesFromTitle(title) {
    const words = normalize(title).split(' ').filter(Boolean);
    if (words.length < 2) return [];
    // Candidate bases: all prefixes except full title
    // We start from the *shorter* ones later for tie-breaking; here just list.
    const out = [];
    for (let i = 1; i <= words.length - 1; i++) {
      out.push(words.slice(0, i).join(' '));
    }
    return out;
  }

  function siblingsForBase(products, base) {
    const prefix = normLower(base) + ' ';
    if (prefix.trim().length < 2) return [];
    return products.filter((p) => {
      if (!p.titleLower.startsWith(prefix) || p.titleLower.length <= prefix.length) return false;
      // Ensure the base is valid for *each* sibling too (prevents mixing groups like "Bella in" vs "Bella in Pelle").
      return isValidBaseForTitle(p.title, base);
    });
  }

  function pickMasterHandle(siblings) {
    return siblings
      .map((s) => s.handle)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'en'))[0];
  }

  function suffixInfoFor(title, baseWordCount) {
    const words = normalize(title).split(' ').filter(Boolean);
    const suffixWords = words.slice(baseWordCount);
    const first = (suffixWords[0] || '').toLowerCase();
    return {
      suffixWords,
      suffixLen: suffixWords.length,
      firstSuffixWord: first,
    };
  }

  function isValidBaseForTitle(title, base) {
    const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
    const { suffixLen, firstSuffixWord } = suffixInfoFor(title, baseWordCount);
    if (suffixLen < 1 || suffixLen > 3) return false;
    if (!firstSuffixWord) return false;
    if (CONNECTOR_WORDS.has(firstSuffixWord)) return false;
    return true;
  }

  async function inferGroupFromTitle(title) {
    const candidates = candidatesFromTitle(title);
    if (!candidates.length) return null;

    // Evaluate all candidates with caching.
    //
    // IMPORTANT (merchant requirement):
    // - The fixed part must match *exactly* (same words, same order) and color is always at the end.
    // - Therefore, we must prefer the *longest* base that yields a valid sibling set.
    //
    // Selection:
    // 1) siblings count desc (ensure the group is stable regardless of which sibling you clicked)
    // 2) max base word count (use the longest fixed part only as a tie-breaker)
    // 3) max suffix words asc (color label should stay short-ish; allows multi-word colors)
    let best = null; // { base, siblings, score }

    for (const base of candidates) {
      // Must be valid for the clicked title itself (prevents choosing "Piattina" -> suffix starts with "Edizione"/"in"/"Charm")
      if (!isValidBaseForTitle(title, base)) continue;

      const products = await fetchSearchProducts(base);
      const siblings = siblingsForBase(products, base);
      if (siblings.length < 2) continue;

      const baseWordCount = normalize(base).split(' ').filter(Boolean).length
      const suffixWordCounts = siblings.map((s) => {
        const info = suffixInfoFor(s.title, baseWordCount);
        return info.suffixLen;
      });
      const maxSuffixWords = Math.max(...suffixWordCounts);
      if (maxSuffixWords > 3) continue;

      const score = {
        count: siblings.length,
        baseWordCount,
        maxSuffixWords,
      };

      if (
        !best ||
        score.count > best.score.count ||
        (score.count === best.score.count && score.baseWordCount > best.score.baseWordCount) ||
        (score.count === best.score.count &&
          score.baseWordCount === best.score.baseWordCount &&
          score.maxSuffixWords < best.score.maxSuffixWords)
      ) {
        best = { base, siblings, score };
      }
    }

    if (!best) return null;
    const master = pickMasterHandle(best.siblings);
    if (!master) return null;
    return { base: best.base, siblings: best.siblings, masterHandle: master };
  }

  function rewriteCardLinks(card, masterHandle, base, clickedHandle) {
    const masterUrl = `/products/${encodeURIComponent(masterHandle)}`;
    const qs = new URLSearchParams();
    qs.set('base', base);
    qs.set('sibling', clickedHandle);
    const href = `${masterUrl}?${qs.toString()}`;

    // Update all anchors inside card that point to the original product URL.
    // In this theme, image and title links are both <a href="{{ product.url }}">
    card.querySelectorAll('a[href]').forEach((a) => {
      const raw = a.getAttribute('href') || '';
      if (!raw.startsWith('/products/')) return;
      a.setAttribute('href', href);
    });

    // Also update data-href used by the theme (collection-aware click handlers)
    card.querySelectorAll('[data-href]').forEach((el) => {
      el.setAttribute('data-href', href);
    });
  }

  async function processCardsOnce() {
    const cards = Array.from(document.querySelectorAll(productCardSelector)).filter(
      (c) => !c.hasAttribute(processedAttr)
    );
    if (!cards.length) return;

    // Only run on pages that render multiple products (collection/search/home lists).
    // If only 1 card exists, nothing to do.
    if (document.querySelectorAll(productCardSelector).length < 2) return;

    // Limit concurrency so we don't spam /search/suggest.json
    const concurrency = 4;
    let i = 0;
    const workers = new Array(concurrency).fill(0).map(async () => {
      while (i < cards.length) {
        const idx = i++;
        const card = cards[idx];
        card.setAttribute(processedAttr, 'true');

        const title = normalize(card.getAttribute('data-product-title'));
        const clickedHandle = card.getAttribute('data-product-handle');
        if (!title || !clickedHandle) continue;

        const group = await inferGroupFromTitle(title);
        if (!group) continue;

        rewriteCardLinks(card, group.masterHandle, group.base, clickedHandle);
      }
    });

    await Promise.all(workers);
  }

  // Run once on load, and also when infinite scroll injects more cards.
  const boot = () => {
    processCardsOnce().catch(() => {});
    const obs = new MutationObserver(() => processCardsOnce().catch(() => {}));
    obs.observe(document.documentElement, { subtree: true, childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();

